import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Alert, Button } from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

import typography from "../../cssUtils/typography";
import { ColorsConstants, borderColors, grayFontColor } from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import { GET_CHANNEL_STATUS, GET_SUBSCRIPTIONS, INSTAGRAM_ACCOUNTS } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import WhatsAppConnectionFlow from "../Dashboard/WhatsAppMaterialUI";
import InstagramConnectWizard from "../InstagramFlow/InstagramConnectWizard";
import InstagramConnectChat from "../InstagramFlow/InstagramConnectChat";
import { useInstagramApi } from "../InstagramFlow/shared";

const CHANNELS = [
  {
    key: "whatsapp",
    name: "WhatsApp",
    icon: WhatsAppIcon,
    color: "#167A4C",
    soft: "#E3F3EA",
    desc: "Send templates, broadcasts and chatbot replies through the WhatsApp Business Platform, and handle chats in the shared inbox.",
    needs: ["Meta Business account", "A phone number that can receive SMS or a call", "Business display name"],
  },
  {
    key: "rcs",
    name: "RCS",
    icon: SmsIcon,
    color: "#1D5FC9",
    soft: "#E4EDFB",
    desc: "Send branded rich messages with images, carousels and buttons to Android phones' default messaging app.",
    needs: ["Brand logo and banner image", "Privacy policy and terms links", "Sample messages for your use case"],
  },
  {
    key: "instagram",
    name: "Instagram",
    icon: InstagramIcon,
    color: "#B02A6B",
    soft: "#FBE7F0",
    desc: "Answer Instagram DMs, story replies and mentions from the shared inbox, and automate them with chatbots and comment-to-DM flows.",
    needs: ["Instagram professional account (Business or Creator)", "Login access to that account", "“Allow access to messages” turned on in Instagram"],
  },
  {
    key: "facebook",
    name: "Facebook",
    icon: FacebookIcon,
    color: "#1F57C3",
    soft: "#E5EDFB",
    desc: "Reply to messages sent to your Facebook Page from the shared inbox, and automate common questions with chatbots.",
    needs: ["A Facebook Page", "Admin access to that Page", "Meta Business account"],
    comingSoon: true,
  },
  {
    key: "email",
    name: "Email",
    icon: EmailIcon,
    color: "#B4460F",
    soft: "#FBEBE0",
    desc: "Send campaigns and transactional email from your own domain.",
    needs: ["A domain you own", "Access to its DNS settings", "A sender address on that domain"],
    comingSoon: true,
  },
];

const MF_PLAN_NAMES = ["elite", "pro"];

// What "Set up WhatsApp" does for a tenant that isn't connected yet:
// tenants that own the WA add-on (trial gets every channel from the backend) or MF plans connect directly;
// paying tenants buy the add-on first.
const getWhatsAppAction = (subscriptionStatus) => {
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  // conversations.view is granted only by WhatsApp addons, so it marks the WA channel
  const hasWaAddon = permissions.includes("conversations.view");
  const canBuy = permissions.includes("billing_subscription_plan.view");
  if (hasWaAddon || MF_PLAN_NAMES.includes(localStorage.getItem("plan"))) return "connect";
  if (["active", "renewal_due"].includes(subscriptionStatus)) return canBuy ? "purchase" : "ask_admin";
  return canBuy ? "renew" : "ask_admin";
};

const WA_ACTION_LABEL = {
  connect: "Set up WhatsApp",
  purchase: "Purchase WhatsApp add-on",
  renew: "Renew plan",
  ask_admin: "Set up WhatsApp",
};

const PILL = {
  connected: { label: "Connected", color: "#167A4C", bg: "#E3F3EA" },
  none: { label: "Not connected", color: "#4A5568", bg: "#F6F8FA" },
  soon: { label: "Coming soon", color: "#8A5A00", bg: "#FFF2D6" },
  reauth: { label: "Needs reconnect", color: "#B42318", bg: "#FDECEA" },
  messaging_off: { label: "Messaging off", color: "#8A5A00", bg: "#FFF2D6" },
};

// Instagram has several accounts per tenant; the card shows the worst state, same order as the account banners.
const instagramState = (accounts) => {
  const live = accounts.filter((a) => a.status !== "DISCONNECTED");
  if (live.some((a) => a.status === "NEEDS_REAUTH")) return "reauth";
  if (live.some((a) => a.status === "MESSAGING_DISABLED")) return "messaging_off";
  return live.length ? "connected" : "none";
};

export default function ChannelOnboarding(props) {
  const { getValidToken } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [waOpen, setWaOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const { request } = useInstagramApi();
  const [ig, setIg] = useState(null); // INSTAGRAM_ACCOUNTS response: accounts, role, env_token_available
  const [igWizard, setIgWizard] = useState(null); // { reconnect?: account }
  const [igOpen, setIgOpen] = useState(false); // account list expanded inside the Instagram card
  const [igListKey, setIgListKey] = useState(0); // remounts the list after the card's own wizard changes accounts

  // Separate from fetchStatus so an Instagram API error never hides WhatsApp / RCS status.
  const fetchInstagram = () =>
    request("get", INSTAGRAM_ACCOUNTS).then(setIg).catch(() => setIg({ accounts: [], role: "" }));

  const fetchStatus = async () => {
    fetchInstagram();
    setLoading(true);
    setError("");
    try {
      const token = await getValidToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-ID": localStorage.getItem("tenant"),
      };
      const [response, subResponse] = await Promise.all([
        fetch(GET_CHANNEL_STATUS, { headers }),
        fetch(GET_SUBSCRIPTIONS, { headers }),
      ]);
      if (!response.ok || !subResponse.ok) throw new Error("Failed to fetch channel status");
      setStatus(await response.json());
      setSubscriptionStatus((await subResponse.json())?.status || null);
    } catch (err) {
      console.error("Error fetching channel status:", err);
      setError("Couldn't load channel status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const waAction = getWhatsAppAction(subscriptionStatus);
  // Label only: without RCS access, "Set up RCS" already ends on the locked page's Purchase Add-on
  const rcsNeedsAddon =
    ["active", "renewal_due"].includes(subscriptionStatus) &&
    !JSON.parse(localStorage.getItem("permissions") || "[]").includes("rcs_bot_create.view");

  const handleSetup = (key) => {
    if (key === "whatsapp") {
      if (waAction === "connect") setWaOpen(true);
      // Opens Subscription; active plans land on add-on selection, others on the plans screen to renew
      else props.onNavigate("billing_subscription", waAction === "purchase" ? { openAddons: true } : {});
    }
    else if (key === "rcs") props.onNavigate("section_direct_rcs_bot_create", { startCreateBot: true });
    else if (key === "instagram") {
      const reauth = igAccounts.find((a) => a.status === "NEEDS_REAUTH");
      if (igAdmin && igState === "none") setIgWizard({});
      else if (igAdmin && reauth) setIgWizard({ reconnect: reauth });
      else setIgOpen((open) => !open);
    }
  };

  const igAccounts = ig?.accounts || [];
  const igState = instagramState(igAccounts);
  const igAdmin = ig?.role === "admin"; // backend allows only admins to connect / reconnect
  const IG_LABEL = {
    none: "Set up Instagram",
    connected: igOpen ? "Hide accounts" : "Manage accounts",
    reauth: igAdmin ? "Reconnect" : igOpen ? "Hide accounts" : "View accounts",
    messaging_off: igOpen ? "Hide accounts" : "See how to fix",
  };
  const connectedNames = igAccounts.filter((a) => a.status !== "DISCONNECTED").map((a) => `@${a.username}`);

  return (
    <Box sx={{ maxWidth: 1040, mx: "auto", py: 4, px: { xs: 2, md: 3 } }}>
      <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2, color: ColorsConstants.primaryBlue100, mb: 0.75 }}>
        Channel Onboarding
      </Typography>
      <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, maxWidth: "62ch" }}>
        Connect the channels you want to reach customers on. Once connected, you can use them in campaigns, chatbots and fallback chains.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mt: 3 }}
          action={<Button color="inherit" size="small" onClick={fetchStatus}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, mt: 3.5 }}>
        {CHANNELS.map((c) => {
          const isWa = c.key === "whatsapp";
          const isIg = c.key === "instagram";
          let state = c.comingSoon ? "soon" : status?.[c.key] ? "connected" : "none";
          if (isIg) state = igState;
          const pill = PILL[state];
          // CTA stays disabled until status is known, so a connected channel is never offered for setup.
          // Instagram's CTA stays live once connected: it opens the account list inside this card.
          const disabled = isIg
            ? !ig || (state === "none" && !igAdmin)
            : state !== "none" || loading || !status || (isWa && waAction === "ask_admin");
          const cardLoading = isIg ? !ig : loading && !c.comingSoon;
          const Icon = c.icon;
          return (
            <Box
              key={c.key}
              component="article"
              aria-labelledby={`channel_${c.key}`}
              sx={{
                backgroundColor: ColorsConstants.primaryWhite,
                border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                borderRadius: "14px",
                p: 2.75,
                display: "grid",
                gridTemplateColumns: { xs: "56px minmax(0,1fr)", md: "56px minmax(0,1fr) auto" },
                gap: 2.5,
                alignItems: "start",
              }}
            >
              <Box sx={{ width: 56, height: 56, borderRadius: "14px", display: "grid", placeItems: "center", backgroundColor: c.soft, color: c.color }}>
                <Icon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography id={`channel_${c.key}`} component="h2" sx={{ fontSize: 18, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>
                  {c.name}
                </Typography>
                <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mt: 0.5, mb: 1.5, maxWidth: "60ch" }}>
                  {c.desc}
                </Typography>
                <Box component="ul" aria-label="What you'll need" sx={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", m: 0, p: 0, listStyle: "none" }}>
                  {c.needs.map((n) => (
                    <Box component="li" key={n} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 13.5, color: grayFontColor }}>
                      <CheckIcon sx={{ fontSize: 14 }} />
                      {n}
                    </Box>
                  ))}
                </Box>
                {isIg && connectedNames.length > 0 && (
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 1.25 }}>
                    Connected: {connectedNames.join(", ")}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  alignItems: { xs: "center", md: "flex-end" },
                  justifyContent: { xs: "space-between", md: "flex-start" },
                  gap: 1.5,
                  minWidth: { md: 170 },
                  gridColumn: { xs: "1 / -1", md: "auto" },
                }}
              >
                {cardLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 12.5, fontWeight: 700,
                      px: 1.25, py: 0.5, borderRadius: "999px", color: pill.color, backgroundColor: pill.bg,
                      "&::before": { content: '""', width: 7, height: 7, borderRadius: "50%", backgroundColor: "currentColor" },
                    }}
                  >
                    {pill.label}
                  </Box>
                )}
                <ButtonOne
                  variant={state === "none" || state === "reauth" ? "contained" : "outlined"}
                  disabled={disabled}
                  startIcon={state === "connected" && !isIg ? <CheckIcon /> : null}
                  onClick={() => handleSetup(c.key)}
                >
                  {isIg ? IG_LABEL[state] : state === "connected" ? "Connected" : state === "soon" ? "Coming soon" : isWa ? WA_ACTION_LABEL[waAction] : c.key === "rcs" && rcsNeedsAddon ? "Purchase RCS Addon" : `Set up ${c.name}`}
                </ButtonOne>
                {isWa && state === "none" && status && waAction === "ask_admin" && (
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor, maxWidth: 200, textAlign: { md: "right" } }}>
                    Ask your admin to purchase the WhatsApp add-on.
                  </Typography>
                )}
                {isIg && ig && !igAdmin && (state === "none" || state === "reauth") && (
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor, maxWidth: 200, textAlign: { md: "right" } }}>
                    {state === "none" ? "Ask a workspace admin to connect Instagram." : "Ask a workspace admin to reconnect."}
                  </Typography>
                )}
              </Box>
              {isIg && igOpen && state !== "none" && (
                <Box sx={{ gridColumn: "1 / -1", borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, pt: 2.5 }}>
                  <InstagramConnectChat key={igListKey} onLoad={setIg} />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {waOpen && (
        <WhatsAppConnectionFlow
          dialogOnly
          onClose={() => setWaOpen(false)}
          onConnectionComplete={fetchStatus}
        />
      )}

      <InstagramConnectWizard
        open={Boolean(igWizard)}
        reconnect={igWizard?.reconnect}
        envTokenAvailable={ig?.env_token_available}
        request={request}
        onClose={(changed) => {
          setIgWizard(null);
          if (changed) {
            fetchInstagram();
            setIgListKey((k) => k + 1);
            setIgOpen(true); // show the new account and its status right away
          }
        }}
      />
    </Box>
  );
}
